import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, UntypedFormControl, FormGroup, Validators } from '@angular/forms';
import { Observable ,  BehaviorSubject } from 'rxjs';


import * as _ from 'lodash';

import { columns } from './columns';
import { SearchModel } from 'shared/search/search.model';
import { SortModel } from 'shared/search/sort.model';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';

import { DigitalAssetsSearchService } from '../../../../digital-assets/search/digital-assets-search.service';
import { BrandService } from 'shared/brand/brand.service';
import { ListManagerService } from 'shared/list-manager/list-manager.service';

import { HiddenScrollComponent } from 'shared/scroll/hidden-scroll/hidden-scroll.component';
import { ListManagerComponent } from 'shared/list-manager/list-manager.component';
import {debounceTime, distinctUntilChanged, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'assigning-assets',
  templateUrl: 'assigning-assets.component.html',
  styleUrls: ['assigning-assets.component.scss'],
  providers: [
    { provide: 'LIST_TYPE', useValue: 'asset' },
    DigitalAssetsSearchService,
    ListManagerService,
  ]
})
export class AssigningAssetsComponent implements OnInit, OnDestroy, OnChanges {

  @HostBinding('class.show')
  @Input() show: boolean = false;

  @Input() assignedAssets: Array<any>;

  @Output() toggleListManager = new EventEmitter<boolean>();

  @ViewChild(HiddenScrollComponent, {static: false})
  hiddenScroll: HiddenScrollComponent | void;

  @ViewChild(ListManagerComponent, {static: false})
  listManager: ListManagerComponent | void;

  searchTermControl = new UntypedFormControl();
  showListManager: boolean = false;
  columns = columns;
  _searchModel: BehaviorSubject<SearchModel>;
  searchModel: SearchModel;
  searchModelObservable: Observable<SearchModel>;
  loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isActive: boolean = true;
  listsData: Array<any>;
  isFiltersHidden: boolean = false;

  get selectAll(): string {
    return this.searchModel.selectAll;
  }
  get currentSortModel(): SortModel {
    return this.searchModel.sortModel;
  }
  get selectedAllChecked(): boolean  {
    return this.searchService.isCurrentPageSelectAllChecked();
  }
  get searchResults(): Array<any> {
    return this.searchService.searchResults;
  }
  get disableItemSelect(): boolean {
    return (this.searchModel.selectAll === 'in-list');
  }
  get searchModelFilters(): Array<any> {
    return this.searchModel && this.searchModel.filters || [];
  }
  get totalRecords(): number {
    return this.searchService.totalRecords;
  }
  get currentPage(): number {
    return this.searchModel.currentPage;
  }
  get itemsPerPage(): number {
    return this.searchModel.itemsPerPage;
  }
  get selectedItems(): Array<any> {
    return this.searchService.selectedItems;
  }
  get brandId(): string {
    return this.brandService.getBrand();
  }

  constructor(
    private searchService: DigitalAssetsSearchService,
    private brandService: BrandService,
    private listsService: ListManagerService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.show) {
      if (changes.show.currentValue) {
      // add already selected items to the search
      this.searchService.clearSelectedItems();
      this.searchService.pushSelectedItems(this.assignedAssets);
      this.runSearch(this.searchModel);
      _.defer(() => {
        if (this.hiddenScroll instanceof HiddenScrollComponent) {
          this.hiddenScroll.defineContentWidth();
        }
        if (this.listManager instanceof ListManagerComponent) {
          this.listManager.updateScroll();
        }
      });
      this.fetchLists();
      } else {
        this.showListManager = false;
      }
    }
  }

  ngOnInit() {
    this.searchModel = new SearchModel();
    this.subscribeSearchModel();
    this.searchTermControl.valueChanges.pipe(
        distinctUntilChanged(),
        takeWhile(() => this.isActive))
    .subscribe((event) => {
      if (!this.show) { return; }
      this.onSearchValueChanged(event);
    });
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  // handlers
  onListIconClick() {
    this.showListManager = !this.showListManager;
    this.toggleListManager.emit(this.showListManager);
  }

  onSortChanged(sortModel: SortModel) {
    this.searchModel.currentPage = 1;
    this.searchModel.sortModel = sortModel;
    this._searchModel.next(this.searchModel);
  }

  onSelectAllChange(isSelected: boolean) {
    this.searchService.searchModel.selectAll = 'on-page';
    this.searchService.selectAll(isSelected);
  }

  onSelectAllClick(select: string) {
    if (select === 'deselect-all') {
      this.searchService.searchModel.selectAll = 'on-page';
      this.searchService.deselectAll();
    } else {
      this.searchService.searchModel.selectAll = select;
      this.searchService.selectAll(true);
    }
  }

  onCheckboxChanged(item: any) {
    if (item.selected) {
      this.searchService.pushSelectedItem(item);
    } else {
      this.searchService.pullSelectedItem(item);
    }
    if (this.searchService.selectedItems.length === 0) {
      this.searchService.allSelected = false;
    }
  }

  onSearchValueChanged(searchValue: string) {
    this.searchModel.searchValue = searchValue;
    this.searchModel.currentPage = 1;
    this.searchService.allSelected = false;
    this._searchModel.next(this.searchModel);
  }

  onPageChange(page: number) {
    if (this.hiddenScroll instanceof HiddenScrollComponent) {
      this.hiddenScroll.scrollTo(0);
    }
    this.searchModel.currentPage = page;
    this._searchModel.next(this.searchModel);
  }

  onItemsPerPageChange(itemsPerPage: number) {
    if (this.hiddenScroll instanceof HiddenScrollComponent) {
      this.hiddenScroll.scrollTo(0);
    }
    this.searchModel.currentPage = 1;
    this.searchModel.itemsPerPage = itemsPerPage;
    this._searchModel.next(this.searchModel);
  }

  onListSelect(selectedList: any) {
    _.remove(this.searchModel.filters);
    if (selectedList.selected) {
      const filter = new ActiveFilterModel(
        selectedList._id,
        selectedList.name,
        'list'
      );
      this.searchModel.filters.push(filter);
    }
    this._searchModel.next(this.searchModel);
  }

  onFilterRemove(filter: ActiveFilterModel) {
    if (this.listManager instanceof ListManagerComponent) {
      this.listManager.unselectList(filter._id);
    }
    this._searchModel.next(this.searchModel);
  }

  onSearchIconClick() {
    if (!this.searchModelFilters.length) { return; }
    this.isFiltersHidden = true;
  }

  onCancelSearchClick() {
    this.isFiltersHidden = false;
  }

  // private
  private subscribeSearchModel(): void {
    this._searchModel = <BehaviorSubject<SearchModel>>new BehaviorSubject(this.searchModel);
    this.searchModelObservable = this._searchModel.asObservable();

    this.searchModelObservable
      .pipe(debounceTime(900),
      takeWhile(() => this.isActive))
      .subscribe((searchModel) => {
        if (!this.show) { return; }
        this.runSearch(searchModel);
      });
  }

  private runSearch(searchModel: SearchModel) {
    this.loading.next(true);
    this.searchService.runSearch(this.brandId, searchModel)
    .subscribe(searchResults => {
      this.loading.next(false);
    });
  }

  private fetchLists() {
    this.listsService.fetch()
    .subscribe((data: any) => {
      this.listsData = data;
    });
  }
}
